import config from "Configurations";
import { apiRequest } from "util/services";
import {
  checkFeedbackApiReady,
  createInstrument,
  updateInstrument,
  deleteInstrument,
  setInstrumentStatus,
} from "../VoiceCaptureManagement/feedbackService";
import {
  SURVEY_STATUS,
  QUESTION_TYPE,
  RATING_SCALE,
} from "./constants";
import {
  channelsToDistribution,
  distributionToChannelPayload,
  mapInstrumentToSurvey,
  surveyToInstrumentPayload,
  normalizeQuestions,
} from "./surveyApiHelpers";

const BASE = "feedback";

function feedbackApiRoot() {
  return (config.BASE_URL || "").replace(/\/+$/, "");
}

// Features the API reports with the instruments list (features.video_answers), kept from the last list.
let surveyFeatures = {};

export const videoAnswersEnabled = () => surveyFeatures.video_answers === true;

async function listSurveyInstruments() {
  const res = await apiRequest(`${BASE}/instruments`, "GET", null, true);
  if (res?.database_ready === false) {
    const err = new Error("FEEDBACK_UNAVAILABLE");
    err.unavailable = true;
    throw err;
  }
  surveyFeatures = res?.features || {};
  const items = (res?.instruments || []).filter((i) => i.type === "survey");
  return items.map((inst) => ({
    ...inst,
    channels: inst.channels || [],
  }));
}

async function getSurveyInstrument(id) {
  const res = await apiRequest(`${BASE}/instruments/${id}`, "GET", null, true);
  if (!res?.instrument) throw new Error("Survey not found");
  return res.instrument;
}

function resolveSurveyStatus(id, payload, currentStatus) {
  const dist = payload.distribution || {};
  const hasPublicChannel = dist.link_qr?.enabled || dist.web_embed?.enabled;
  const status = payload.status || currentStatus || SURVEY_STATUS.DRAFT;

  if (hasPublicChannel && status === SURVEY_STATUS.DRAFT) {
    return SURVEY_STATUS.ACTIVE;
  }
  return status;
}

async function persistSurvey(id, payload) {
  let currentStatus = payload.status;
  if (id && !currentStatus) {
    try {
      const existing = await getSurveyInstrument(id);
      currentStatus = existing.status;
    } catch (_) {
      // keep draft default below
    }
  }

  const nextStatus = resolveSurveyStatus(id, payload, currentStatus);
  const instrumentPayload = {
    ...surveyToInstrumentPayload({ ...payload, status: nextStatus }),
    status: nextStatus,
  };
  const inst = id
    ? await updateInstrument(id, instrumentPayload)
    : await createInstrument(instrumentPayload);

  const channelRes = await apiRequest(
    `${BASE}/instruments/${inst.id}/channels`,
    "PUT",
    { channels: distributionToChannelPayload(payload.distribution) },
    true
  );
  if (!channelRes?.instrument) {
    throw new Error(channelRes?.error || "Channel update failed");
  }
  return mapInstrumentToSurvey(channelRes.instrument);
}

export async function checkSurveyApiReady() {
  return checkFeedbackApiReady();
}

export async function listSurveys() {
  const items = await listSurveyInstruments();
  const detailed = await Promise.all(
    items.map(async (row) => {
      try {
        const full = await getSurveyInstrument(row.id);
        return mapInstrumentToSurvey(full);
      } catch (_) {
        return mapInstrumentToSurvey(row);
      }
    })
  );
  return detailed.sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );
}

export async function getSurvey(id) {
  const inst = await getSurveyInstrument(id);
  return mapInstrumentToSurvey(inst);
}

export async function createSurvey(payload) {
  return persistSurvey(null, {
    ...payload,
    status: payload.status || SURVEY_STATUS.DRAFT,
  });
}

export async function updateSurvey(id, payload) {
  return persistSurvey(id, payload);
}

export async function deleteSurvey(id) {
  await deleteInstrument(id);
  return true;
}

export async function duplicateSurvey(id) {
  const source = await getSurvey(id);
  const copy = await createSurvey({
    name: `${source.name} (Copy)`,
    description: source.description,
    widgetHeaderTitle: source.widgetHeaderTitle,
    showBranding: source.showBranding,
    questions: source.questions.map((q) => ({ ...q, id: undefined })),
    responseMode: source.responseMode,
    multilingualEnabled: source.multilingualEnabled,
    primaryLanguage: source.primaryLanguage,
    editingLanguage: source.primaryLanguage,
    translations: source.translations,
    distribution: {
      ...source.distribution,
      link_qr: { ...source.distribution.link_qr, enabled: false, slug: "", fullUrl: "" },
    },
    status: SURVEY_STATUS.DRAFT,
  });
  return copy;
}

export async function setSurveyStatus(id, status) {
  const inst = await setInstrumentStatus(id, status);
  const full = await getSurveyInstrument(inst.id);
  return mapInstrumentToSurvey(full);
}

// ── CRM-triggered calls (phone_call channel "trigger") ──────────────────────
export const getPhoneTrigger = (instrumentId) =>
  apiRequest(`${BASE}/instruments/${instrumentId}/phone-trigger`, "GET", null, true);

export const queuePhoneTriggerTest = (instrumentId, payload) =>
  apiRequest(`${BASE}/instruments/${instrumentId}/phone-trigger/test`, "POST", payload, true);

export const cancelPhoneTriggerCall = (instrumentId, rowId) =>
  apiRequest(`${BASE}/instruments/${instrumentId}/phone-trigger/queue/${rowId}/cancel`, "POST", {}, true);

/** Rename the survey link; resolves {channel} or {error: "slug_taken" | "slug_invalid" | …}. */
export const setLinkSlug = (instrumentId, slug) =>
  apiRequest(`${BASE}/instruments/${instrumentId}/channels/link_qr/slug`, "PUT", { slug }, true);

// Kiosk mode on/off; the returned channel's config carries kiosk_key while it is on.
export const setLinkKiosk = (instrumentId, enabled) =>
  apiRequest(`${BASE}/instruments/${instrumentId}/channels/link_qr/kiosk`, "PUT", { enabled }, true);

export async function regenerateLinkQr(instrumentId) {
  const res = await apiRequest(
    `${BASE}/instruments/${instrumentId}/channels/link_qr/regenerate`,
    "POST",
    {},
    true
  );
  if (!res?.channel) throw new Error(res?.error || "Regenerate failed");
  return res.channel;
}

export async function fetchPublicInstrument(slug, { embed = false, kioskKey = "" } = {}) {
  const params = new URLSearchParams();
  if (embed) params.set("embed", "1");
  if (kioskKey) params.set("kiosk", kioskKey);
  const query = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(
    `${feedbackApiRoot()}/feedback/public/instrument/${encodeURIComponent(slug)}${query}`,
    { credentials: "omit" }
  );
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body };
}

export async function requestCaptchaPass(slug, captchaToken) {
  const res = await fetch(`${feedbackApiRoot()}/feedback/public/captcha-pass`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    body: JSON.stringify({ slug, captcha_token: captchaToken }),
  });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body };
}

// Whether this address already answered a one-response-per-email survey, asked as soon as it is
// typed (the submit still refuses a duplicate).
export async function checkSurveyEmail(slug, email, captchaPass, kioskKey = "") {
  const res = await fetch(`${feedbackApiRoot()}/feedback/public/email-check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    body: JSON.stringify({
      slug,
      email,
      captcha_pass: captchaPass || undefined,
      kiosk_key: kioskKey || undefined,
    }),
  });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body };
}

const VISITOR_STORAGE_KEY = "pv_survey_visitor";

// A random id kept in this browser, so the survey list counts a browser once however often it
// reloads the link. Without storage (private mode) each page load gets its own.
function surveyVisitorId() {
  const fresh = () =>
    (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`).replace(/[^A-Za-z0-9_-]/g, "");
  try {
    let id = window.localStorage.getItem(VISITOR_STORAGE_KEY);
    if (!id || !/^[A-Za-z0-9_-]{16,64}$/.test(id)) {
      id = fresh();
      window.localStorage.setItem(VISITOR_STORAGE_KEY, id);
    }
    return id;
  } catch (_) {
    return fresh();
  }
}

/** Count this browser's visit to a survey link; `started` once it gives its first answer. */
export function recordSurveyVisit(slug, { started = false } = {}) {
  if (!slug || typeof window === "undefined") return;
  fetch(`${feedbackApiRoot()}/feedback/public/survey-visit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    keepalive: true,
    body: JSON.stringify({ slug, visitor: surveyVisitorId(), started }),
  }).catch(() => {});
}

export async function submitPublicSurveyResponse(payload) {
  const res = await fetch(`${feedbackApiRoot()}/feedback/public/responses`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "omit",
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body };
}

export function collectClientDeviceMeta() {
  if (typeof window === "undefined") return {};
  try {
    return {
      language: navigator.language || "",
      platform: navigator.platform || "",
      screen: window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : "",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    };
  } catch (_) {
    return {};
  }
}

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

export function parseUtmParams(urlString) {
  if (!urlString || typeof window === "undefined") return null;
  try {
    const url = new URL(urlString, window.location.href);
    const utm = {};
    UTM_KEYS.forEach((key) => {
      const value = url.searchParams.get(key);
      if (value) utm[key] = value;
    });
    return Object.keys(utm).length ? utm : null;
  } catch (_) {
    return null;
  }
}

export function collectAttributionMeta() {
  if (typeof window === "undefined") return {};
  const pageUrl = window.location.href || "";
  const referrer = (document.referrer || "").trim();
  return {
    page_url: pageUrl,
    referrer_url: referrer || null,
    utm_params: parseUtmParams(pageUrl),
  };
}

export function buildSessionContext({
  widgetOpenedAtMs,
  attribution = {},
  timeOnPageBeforeTriggerSeconds,
} = {}) {
  const context = { ...attribution };
  if (widgetOpenedAtMs) {
    context.completion_time_seconds = Math.max(
      0,
      Math.round((Date.now() - widgetOpenedAtMs) / 1000)
    );
  }
  if (timeOnPageBeforeTriggerSeconds != null) {
    context.time_on_page_before_trigger_seconds = Math.max(
      0,
      Math.round(Number(timeOnPageBeforeTriggerSeconds) || 0)
    );
  }
  return context;
}

export function makeBlankQuestion() {
  return {
    id: `q_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    type: QUESTION_TYPE.SINGLE_CHOICE,
    text: "",
    required: false,
    ratingScale: RATING_SCALE.ONE_TO_FIVE,
    options: ["", ""],
  };
}

export { normalizeQuestions, mapInstrumentToSurvey, channelsToDistribution };
